import { useEffect, useRef, useState, useCallback } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Camera, LayoutDashboard } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useDirectionStore } from '@/store/directionStore'
import { useAuthStore } from '@/store/authStore'
import { queryClient } from '@/main'
import { useCommandPaletteStore } from './commandPaletteStore'
import { MAIN_NAV_ROUTES, ADMIN_NAV_ROUTES, SETTINGS_NAV_ROUTE, type NavRoute } from '@/components/layout/variants/navRoutes'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PaletteItem {
  id: string
  label: string
  href: string
  Icon: LucideIcon
  kind: 'page' | 'camera'
}

// ---------------------------------------------------------------------------
// Pure filter — exported for unit testing
// ---------------------------------------------------------------------------

const MAX_RESULTS = 12

export function filterCommands(items: PaletteItem[], query: string): PaletteItem[] {
  if (!query.trim()) return items.slice(0, MAX_RESULTS)
  const lower = query.toLowerCase()
  return items
    .filter(item => item.label.toLowerCase().includes(lower))
    .slice(0, MAX_RESULTS)
}

// ---------------------------------------------------------------------------
// Camera extraction from React Query cache
// ---------------------------------------------------------------------------

interface CameraLike {
  id: number | string
  name: string
}

function extractCamerasFromCache(): CameraLike[] {
  // Fetch all cache entries whose key starts with 'cameras'
  const allEntries = queryClient.getQueriesData<unknown>({ queryKey: ['cameras'] })
  const seen = new Set<string>()
  const results: CameraLike[] = []

  for (const [, data] of allEntries) {
    if (!data) continue

    // Shape 1: direct array of camera objects
    if (Array.isArray(data)) {
      for (const item of data) {
        if (isCameraLike(item)) pushCam(item, seen, results)
      }
      continue
    }

    // Shape 2: paginated { results: [...] }
    if (typeof data === 'object' && data !== null && 'results' in data) {
      const { results: rows } = data as { results: unknown }
      if (Array.isArray(rows)) {
        for (const item of rows) {
          if (isCameraLike(item)) pushCam(item, seen, results)
        }
        continue
      }
    }

    // Shape 3: infinite query pages: { pages: [{ results: [...] }] }
    if (typeof data === 'object' && data !== null && 'pages' in data) {
      const { pages } = data as { pages: unknown[] }
      if (Array.isArray(pages)) {
        for (const page of pages) {
          if (typeof page === 'object' && page !== null && 'results' in page) {
            const { results: rows } = page as { results: unknown }
            if (Array.isArray(rows)) {
              for (const item of rows) {
                if (isCameraLike(item)) pushCam(item, seen, results)
              }
            }
          }
        }
      }
    }
  }

  return results
}

function isCameraLike(x: unknown): x is CameraLike {
  return (
    typeof x === 'object' &&
    x !== null &&
    ('id' in x) &&
    ('name' in x) &&
    typeof (x as CameraLike).name === 'string'
  )
}

function pushCam(cam: CameraLike, seen: Set<string>, out: CameraLike[]) {
  const key = String(cam.id)
  if (!seen.has(key)) {
    seen.add(key)
    out.push(cam)
  }
}

// ---------------------------------------------------------------------------
// Focus guard (same logic as DirectionSwitcher Shift+D guard)
// ---------------------------------------------------------------------------

function isTypingTarget(el: Element | null): boolean {
  if (!el) return false
  const tag = (el as HTMLElement).tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
  if ((el as HTMLElement).isContentEditable) return true
  return false
}

// ---------------------------------------------------------------------------
// CommandPalette component
// ---------------------------------------------------------------------------

export function CommandPalette() {
  const { t } = useTranslation('common')
  // Cast t to accept any string key — navRoutes uses dynamic keys not in the strict union
  const tAny = t as (k: string) => string
  const navigate = useNavigate()
  const direction = useDirectionStore(s => s.direction)
  const { user } = useAuthStore()
  const { open, setOpen } = useCommandPaletteStore()

  const [query, setQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)

  // Build the nav-route palette items from the shared route definition
  const navItems: PaletteItem[] = (() => {
    const routes: NavRoute[] = [
      ...MAIN_NAV_ROUTES,
      ...(user?.is_superuser ? ADMIN_NAV_ROUTES : []),
      SETTINGS_NAV_ROUTE,
    ]
    return routes.map(r => ({
      id: `page:${r.href}`,
      label: r.labelLiteral ?? tAny(r.labelKey),
      href: r.href,
      Icon: r.Icon,
      kind: 'page' as const,
    }))
  })()

  // Camera items from cache — computed once on each open (cache snapshot)
  const [cameraItems, setCameraItems] = useState<PaletteItem[]>([])
  useEffect(() => {
    if (open) {
      const cams = extractCamerasFromCache()
      setCameraItems(
        cams.map(c => ({
          id: `camera:${c.id}`,
          label: c.name,
          href: '/cameras',
          Icon: Camera,
          kind: 'camera' as const,
        }))
      )
      setQuery('')
      setSelectedIndex(0)
    }
  }, [open])

  const allItems: PaletteItem[] = [...navItems, ...cameraItems]
  const filtered = filterCommands(allItems, query)

  // Keep selectedIndex in bounds when filtered list changes
  useEffect(() => {
    setSelectedIndex(prev => Math.min(prev, Math.max(filtered.length - 1, 0)))
  }, [filtered.length])

  // Activate the selected item
  const activate = useCallback((item: PaletteItem) => {
    setOpen(false)
    navigate(item.href)
  }, [navigate, setOpen])

  // ⌘K / Ctrl+K keyboard handler — only attaches listener when direction is saas
  useEffect(() => {
    if (direction !== 'saas') return
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        // Guard typing targets, but NOT when the palette is already open
        // (it autofocuses its own input, so the guard would block the close toggle)
        if (!open && isTypingTarget(document.activeElement)) return
        e.preventDefault()
        setOpen(!open)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [direction, open, setOpen])

  // Arrow key + Enter navigation inside the palette
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex(i => (i + 1) % Math.max(filtered.length, 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex(i => (i - 1 + Math.max(filtered.length, 1)) % Math.max(filtered.length, 1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const item = filtered[selectedIndex]
      if (item) activate(item)
    }
  }, [filtered, selectedIndex, activate])

  // Auto-focus input when open
  useEffect(() => {
    if (open) {
      // Small RAF to let Radix animate in before focusing
      const raf = requestAnimationFrame(() => inputRef.current?.focus())
      return () => cancelAnimationFrame(raf)
    }
  }, [open])

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/30" />
        <Dialog.Content
          aria-describedby={undefined}
          className={cn(
            'fixed left-1/2 top-24 -translate-x-1/2 z-50',
            'w-[min(92vw,560px)] rounded-lg border border-border bg-popover shadow-elevation-3 overflow-hidden'
          )}
          onKeyDown={handleKeyDown}
        >
          {/* Visually-hidden title for screen-reader accessibility */}
          <Dialog.Title className="sr-only">
            {t('command.title')}
          </Dialog.Title>

          {/* Search input */}
          <div className="flex items-center border-b border-border">
            <LayoutDashboard className="ml-3 h-4 w-4 text-muted-foreground flex-shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={e => {
                setQuery(e.target.value)
                setSelectedIndex(0)
              }}
              placeholder={t('command.placeholder')}
              className="h-11 flex-1 px-3 text-[13px] bg-transparent outline-none w-full placeholder:text-muted-foreground"
            />
          </div>

          {/* Results list */}
          <div className="max-h-72 overflow-y-auto p-1" role="listbox">
            {filtered.length === 0 ? (
              <p className="px-3 py-6 text-center text-[13px] text-muted-foreground">
                {t('command.noResults')}
              </p>
            ) : (
              filtered.map((item, index) => (
                <button
                  key={item.id}
                  role="option"
                  aria-selected={index === selectedIndex}
                  onClick={() => activate(item)}
                  className={cn(
                    'w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-[13px] text-left transition-colors duration-75',
                    index === selectedIndex
                      ? 'bg-muted text-foreground'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  )}
                >
                  <item.Icon className="h-4 w-4 flex-shrink-0" />
                  <span className="flex-1 truncate">{item.label}</span>
                  <span className="text-[10px] uppercase text-muted-foreground ml-auto flex-shrink-0">
                    {item.kind}
                  </span>
                </button>
              ))
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
